import React, { ReactNode } from 'react';

// Assets
import Arms from 'url:../assets/images/injuries/arms.webp';
import Shoulders from 'url:../assets/images/injuries/shoulders.webp';
import Chest from 'url:../assets/images/injuries/chest.webp';
import Core from 'url:../assets/images/injuries/core.webp';
import Back from 'url:../assets/images/injuries/back.webp';
import Glutes from 'url:../assets/images/injuries/glutes.webp';
import Legs from 'url:../assets/images/injuries/legs.webp';

export type User = {
  _id: string;
  name: string;
  avatar: string;
};

export type Step = {
  _id: number;
  name: string;
  avatar: string;
};

export const trainerProgramUser: User[] = [
  {
    _id: 'emily_demo',
    name: 'Emily Thompson (Demo)',
    avatar: 'https://i.pravatar.cc/128?img=5',
  },
  {
    _id: 'oliver_demo',
    name: 'Oliver Bennett (Demo)',
    avatar: 'https://i.pravatar.cc/128?img=3',
  },
];

export const trainerProgramStep: Step[] = [];

export type Level = {
  name: string;
  emoji: string;
  descriptions: string[];
};

export const fitnessLevels: Level[] = [
  {
    name: 'Beginner',
    emoji: '🟡',
    descriptions: [
      'New to training or inconsistent experience',
      'Learning basic movement patterns and form',
      'Low strength and endurance base',
      'Needs simple, guided programs',
    ],
  },
  {
    name: 'Early Intermediate',
    emoji: '🟠',
    descriptions: [
      'Training consistently for a few months',
      'Improving technique in major exercises',
      'Building strength and confidence',
      'Can handle slightly structured programs',
    ],
  },
  {
    name: 'Intermediate',
    emoji: '🟡',
    descriptions: [
      'Regular training (several months to ~2 years)',
      'Good technique in most exercises',
      'Can follow structured programs',
      'Starting to hit plateaus → needs progression strategies',
    ],
  },
  {
    name: 'Advanced',
    emoji: '⚫',
    descriptions: [
      'Several years of consistent training',
      'Strong technique across all major lifts',
      'Requires periodized and goal-specific programming',
      'Progress is slower → needs precise optimization',
    ],
  },
  {
    name: 'Athlete',
    emoji: '⚪',
    descriptions: [
      'Training is sport-specific and performance-driven',
      'High level of strength, endurance, and skill',
      'Follows structured, coach-led programs',
      'Focus on peak performance, recovery, and injury prevention',
    ],
  },
];

export type ActivityLike = {
  name: string;
  value: string;
  descriptions: string[];
};

export const trainingActivityLike: ActivityLike[] = [
  {
    name: 'Mostly sedentary',
    value: 'mostly_sedentary',
    descriptions: [
      'Spends most of the day sitting (desk job, minimal movement).',
      'Little to no structured exercise.',
    ],
  },
  {
    name: 'Lightly active',
    value: 'lightly_active',
    descriptions: [
      'Includes some daily movement (walking, chores, light tasks).',
      'Occasional low-intensity exercise or short workouts.',
    ],
  },
  {
    name: 'Highly active',
    value: 'highly_active',
    descriptions: [
      'Regular physical activity or physically demanding routine.',
      'Frequent workouts, sports, or labor-intensive work.',
    ],
  },
];

export type MovementGrade = {
  name: string;
  value: string;
};

export const trainingMovementGrade: MovementGrade[] = [
  { name: 'Pain', value: 'pain' },
  { name: 'Limited', value: 'limited' },
  { name: 'Good', value: 'good' },
];

export type StyleItem = {
  name: string;
  value: string;
  icon: any;
};

export type Goal = {
  name: string;
  value: string;
  icon: ReactNode;
  styles: StyleItem[];
};

export const trainingGoal: Goal[] = [
  {
    name: 'Build muscle',
    value: 'build-muscle',
    icon: (
      <svg
        width={30}
        height={30}
        viewBox="0 0 30 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <mask
          id="mask0_708_339"
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x={5}
          y={5}
          width={20}
          height={20}
        >
          <path d="M24.375 5.625H5.625V24.375H24.375V5.625Z" fill="white" />
        </mask>
        <g mask="url(#mask0_708_339)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.775 9.57305C9.97461 9.45313 10.282 9.33594 10.7031 9.33594C11.1242 9.33594 11.4316 9.45313 11.6312 9.57305C11.9395 9.75743 12.0316 10.073 12.0363 10.298C12.0453 10.7465 12.0703 12.2254 12.0703 15C12.0703 17.7746 12.0453 19.2535 12.0363 19.702C12.0316 19.9265 11.9395 20.2426 11.6312 20.427C11.4316 20.5469 11.1242 20.664 10.7031 20.664C10.282 20.664 9.97461 20.5469 9.775 20.427C9.4668 20.2426 9.37461 19.927 9.36992 19.702C9.36094 19.2539 9.33594 17.7746 9.33594 15C9.33594 12.2254 9.36094 10.7465 9.36992 10.298C9.37461 10.0734 9.4668 9.75743 9.775 9.57305ZM20.2251 9.57305C20.0254 9.45313 19.7179 9.33594 19.2969 9.33594C18.8758 9.33594 18.5683 9.45313 18.3688 9.57305C18.0609 9.75743 17.9683 10.073 17.9637 10.298C17.9547 10.7461 17.9297 12.2254 17.9297 15C17.9297 17.7746 17.9547 19.2535 17.9637 19.702C17.9683 19.9265 18.0606 20.2426 18.3688 20.427C18.5683 20.5469 18.8758 20.664 19.2969 20.664C19.7179 20.664 20.0254 20.5469 20.2251 20.427C20.5328 20.2426 20.6254 19.927 20.6301 19.702C20.6391 19.2535 20.6641 17.7746 20.6641 15C20.6641 12.2254 20.6391 10.7465 20.6301 10.298C20.6254 10.0734 20.5328 9.75743 20.2251 9.57305ZM7.38281 11.2891C7.125 11.2891 6.91836 11.3359 6.76016 11.3973C6.36329 11.5512 6.24062 11.927 6.23477 12.1785C6.22696 12.5289 6.21094 13.4274 6.21094 15C6.21094 16.5726 6.22656 17.4711 6.23437 17.8215C6.24024 18.073 6.36329 18.4493 6.76016 18.6028C6.91836 18.6641 7.125 18.711 7.38281 18.711C7.64062 18.711 7.84727 18.6641 8.00547 18.6028C8.40235 18.4493 8.525 18.073 8.53086 17.8215C8.53867 17.4711 8.55469 16.5726 8.55469 15C8.55469 13.4274 8.53906 12.5289 8.53125 12.1785C8.5254 11.927 8.40235 11.5508 8.00547 11.3973C7.80639 11.323 7.59528 11.2863 7.38281 11.2891ZM23.2398 11.3973C23.0408 11.323 22.8297 11.2863 22.6172 11.2891C22.3594 11.2891 22.1527 11.3359 21.9946 11.3973C21.5981 11.5512 21.4751 11.927 21.4692 12.1785C21.4613 12.5289 21.4453 13.4274 21.4453 15C21.4453 16.5726 21.4609 17.4711 21.4688 17.8215C21.4746 18.073 21.5977 18.4493 21.9946 18.6028C22.1527 18.6641 22.3598 18.711 22.6172 18.711C22.8751 18.711 23.0817 18.6641 23.2398 18.6028C23.6363 18.4493 23.7594 18.073 23.7652 17.8215C23.7731 17.4711 23.7891 16.5726 23.7891 15C23.7891 13.4274 23.7734 12.5289 23.7657 12.1785C23.7598 11.927 23.6367 11.5508 23.2398 11.3973ZM12.8516 16.116V13.884C12.8516 13.5136 13.1547 13.3191 13.4777 13.2914C13.5444 13.2851 13.6441 13.2784 13.7766 13.2711C14.0422 13.2563 14.4433 13.2422 15.0001 13.2422C15.5567 13.2422 15.9582 13.2563 16.2234 13.2711C16.3559 13.2784 16.4556 13.2851 16.5223 13.2914C16.8453 13.3187 17.1484 13.5136 17.1484 13.884V16.116C17.1484 16.4868 16.8453 16.6809 16.5223 16.7086C16.4556 16.7149 16.3559 16.7216 16.2234 16.7289C15.9578 16.7437 15.5567 16.7578 15.0001 16.7578C14.4433 16.7578 14.0418 16.7437 13.7766 16.7289C13.6441 16.7216 13.5444 16.7149 13.4777 16.7086C13.1547 16.6813 12.8516 16.4864 12.8516 16.116Z"
            fill="currentColor"
          />
        </g>
      </svg>
    ),
    styles: [
      {
        name: 'Weight training',
        value: 'weight-training',
        icon: (
          <svg
            width={30}
            height={30}
            viewBox="0 0 30 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <mask
              id="mask0_708_339"
              style={{ maskType: 'luminance' }}
              maskUnits="userSpaceOnUse"
              x={5}
              y={5}
              width={20}
              height={20}
            >
              <path d="M24.375 5.625H5.625V24.375H24.375V5.625Z" fill="white" />
            </mask>
            <g mask="url(#mask0_708_339)">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.775 9.57305C9.97461 9.45313 10.282 9.33594 10.7031 9.33594C11.1242 9.33594 11.4316 9.45313 11.6312 9.57305C11.9395 9.75743 12.0316 10.073 12.0363 10.298C12.0453 10.7465 12.0703 12.2254 12.0703 15C12.0703 17.7746 12.0453 19.2535 12.0363 19.702C12.0316 19.9265 11.9395 20.2426 11.6312 20.427C11.4316 20.5469 11.1242 20.664 10.7031 20.664C10.282 20.664 9.97461 20.5469 9.775 20.427C9.4668 20.2426 9.37461 19.927 9.36992 19.702C9.36094 19.2539 9.33594 17.7746 9.33594 15C9.33594 12.2254 9.36094 10.7465 9.36992 10.298C9.37461 10.0734 9.4668 9.75743 9.775 9.57305ZM20.2251 9.57305C20.0254 9.45313 19.7179 9.33594 19.2969 9.33594C18.8758 9.33594 18.5683 9.45313 18.3688 9.57305C18.0609 9.75743 17.9683 10.073 17.9637 10.298C17.9547 10.7461 17.9297 12.2254 17.9297 15C17.9297 17.7746 17.9547 19.2535 17.9637 19.702C17.9683 19.9265 18.0606 20.2426 18.3688 20.427C18.5683 20.5469 18.8758 20.664 19.2969 20.664C19.7179 20.664 20.0254 20.5469 20.2251 20.427C20.5328 20.2426 20.6254 19.927 20.6301 19.702C20.6391 19.2535 20.6641 17.7746 20.6641 15C20.6641 12.2254 20.6391 10.7465 20.6301 10.298C20.6254 10.0734 20.5328 9.75743 20.2251 9.57305ZM7.38281 11.2891C7.125 11.2891 6.91836 11.3359 6.76016 11.3973C6.36329 11.5512 6.24062 11.927 6.23477 12.1785C6.22696 12.5289 6.21094 13.4274 6.21094 15C6.21094 16.5726 6.22656 17.4711 6.23437 17.8215C6.24024 18.073 6.36329 18.4493 6.76016 18.6028C6.91836 18.6641 7.125 18.711 7.38281 18.711C7.64062 18.711 7.84727 18.6641 8.00547 18.6028C8.40235 18.4493 8.525 18.073 8.53086 17.8215C8.53867 17.4711 8.55469 16.5726 8.55469 15C8.55469 13.4274 8.53906 12.5289 8.53125 12.1785C8.5254 11.927 8.40235 11.5508 8.00547 11.3973C7.80639 11.323 7.59528 11.2863 7.38281 11.2891ZM23.2398 11.3973C23.0408 11.323 22.8297 11.2863 22.6172 11.2891C22.3594 11.2891 22.1527 11.3359 21.9946 11.3973C21.5981 11.5512 21.4751 11.927 21.4692 12.1785C21.4613 12.5289 21.4453 13.4274 21.4453 15C21.4453 16.5726 21.4609 17.4711 21.4688 17.8215C21.4746 18.073 21.5977 18.4493 21.9946 18.6028C22.1527 18.6641 22.3598 18.711 22.6172 18.711C22.8751 18.711 23.0817 18.6641 23.2398 18.6028C23.6363 18.4493 23.7594 18.073 23.7652 17.8215C23.7731 17.4711 23.7891 16.5726 23.7891 15C23.7891 13.4274 23.7734 12.5289 23.7657 12.1785C23.7598 11.927 23.6367 11.5508 23.2398 11.3973ZM12.8516 16.116V13.884C12.8516 13.5136 13.1547 13.3191 13.4777 13.2914C13.5444 13.2851 13.6441 13.2784 13.7766 13.2711C14.0422 13.2563 14.4433 13.2422 15.0001 13.2422C15.5567 13.2422 15.9582 13.2563 16.2234 13.2711C16.3559 13.2784 16.4556 13.2851 16.5223 13.2914C16.8453 13.3187 17.1484 13.5136 17.1484 13.884V16.116C17.1484 16.4868 16.8453 16.6809 16.5223 16.7086C16.4556 16.7149 16.3559 16.7216 16.2234 16.7289C15.9578 16.7437 15.5567 16.7578 15.0001 16.7578C14.4433 16.7578 14.0418 16.7437 13.7766 16.7289C13.6441 16.7216 13.5444 16.7149 13.4777 16.7086C13.1547 16.6813 12.8516 16.4864 12.8516 16.116Z"
                fill="currentColor"
              />
            </g>
          </svg>
        ),
      },
      {
        name: 'Functional training',
        value: 'functional-training',
        icon: (
          <svg
            width={30}
            height={30}
            viewBox="0 0 30 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <mask
              id="mask0_736_297"
              style={{ maskType: 'luminance' }}
              maskUnits="userSpaceOnUse"
              x={5}
              y={5}
              width={20}
              height={20}
            >
              <path d="M24.375 5.625H5.625V24.375H24.375V5.625Z" fill="white" />
            </mask>
            <g mask="url(#mask0_736_297)">
              <path
                d="M18.9893 13.2704C17.8943 12.4721 17.0227 11.4027 16.5357 10.1393C15.9754 8.70009 15.7996 7.24991 15.7447 6.43326C15.7264 5.95353 15.3235 5.57999 14.8438 5.5983C14.3641 5.61662 13.9905 6.01945 14.0089 6.49918C14.0272 10.11 15.7153 12.8932 15.7153 12.8932L8.58523 22.8395C8.27765 23.2716 8.3765 23.8722 8.80498 24.1799C9.21875 24.4765 9.7937 24.3995 10.1123 24.0004L14.6094 18.4193L17.1436 20.3822L18.125 23.6672C18.2496 24.0846 18.634 24.3703 19.0699 24.3703C19.6155 24.3703 20.055 23.9272 20.055 23.3815C20.055 23.3119 20.0476 23.246 20.033 23.1801C19.1907 19.1225 17.4439 17.8261 17.4439 17.8261L18.7549 16.3393C19.3519 18.0165 19.5349 19.5875 19.5862 20.4371C19.6082 20.8217 19.9048 21.133 20.2857 21.1769C20.7215 21.2282 21.117 20.9169 21.1683 20.4811C21.1719 20.4591 21.1719 20.4335 21.1719 20.4079C21.3001 15.6545 19.8426 13.893 18.9893 13.2704Z"
                fill="currentColor"
              />
              <path
                d="M20.9385 12.0293C21.6978 11.3799 21.8199 10.2765 21.2112 9.5647C20.6026 8.85303 19.4936 8.80255 18.7343 9.45198C17.9749 10.1015 17.8528 11.2049 18.4614 11.9166C19.0701 12.6282 20.1792 12.6787 20.9385 12.0293Z"
                fill="currentColor"
              />
            </g>
          </svg>
        ),
      },
    ],
  },
  {
    name: 'Fat Loss',
    value: 'fat-loss',
    icon: (
      <svg
        width={30}
        height={30}
        viewBox="0 0 30 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <mask
          id="mask0_708_346"
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x={5}
          y={5}
          width={20}
          height={20}
        >
          <path d="M24.375 5.625H5.625V24.375H24.375V5.625Z" fill="white" />
        </mask>
        <g mask="url(#mask0_708_346)">
          <path
            d="M10.2428 18.9211C10.2332 18.8242 10.188 18.7346 10.1161 18.6695C10.0442 18.6044 9.95082 18.5686 9.85412 18.5691H8.01935C7.92265 18.5686 7.82925 18.6044 7.75737 18.6695C7.68549 18.7346 7.6403 18.8242 7.63064 18.9211L7.19528 23.0744C7.17233 23.3184 7.2003 23.5645 7.27738 23.797C7.35447 24.0295 7.47897 24.2433 7.6429 24.4245C7.80685 24.6058 8.00662 24.7506 8.22942 24.8498C8.45222 24.9488 8.69313 25 8.93674 25C9.18034 25 9.42125 24.9488 9.64405 24.8498C9.86685 24.7506 10.0666 24.6058 10.2306 24.4245C10.3945 24.2433 10.519 24.0295 10.5961 23.797C10.6732 23.5645 10.7011 23.3184 10.6782 23.0744L10.2428 18.9211ZM23.1173 23.0744L22.6819 18.9211C22.6722 18.8242 22.627 18.7346 22.5552 18.6695C22.4833 18.6044 22.3898 18.5686 22.2932 18.5691H20.4584C20.3617 18.5686 20.2683 18.6044 20.1964 18.6695C20.1245 18.7346 20.0793 18.8242 20.0697 18.9211L19.6343 23.0744C19.6114 23.3184 19.6393 23.5645 19.7164 23.797C19.7935 24.0295 19.918 24.2433 20.0819 24.4245C20.2459 24.6058 20.4457 24.7506 20.6684 24.8498C20.8913 24.9488 21.1322 25 21.3758 25C21.6194 25 21.8603 24.9488 22.083 24.8498C22.3059 24.7506 22.5057 24.6058 22.6697 24.4245C22.8335 24.2433 22.958 24.0295 23.0352 23.797C23.1122 23.5645 23.1402 23.3184 23.1173 23.0744ZM18.1417 8.08809C18.8664 8.37666 19.4882 8.87776 19.9262 9.52634C20.3643 10.1749 20.5984 10.941 20.5983 11.7252V15.2449C20.5983 15.3486 20.5574 15.4481 20.4845 15.5215C20.4115 15.5948 20.3127 15.636 20.2097 15.636C20.1522 15.6369 20.0954 15.6499 20.0432 15.6741C19.991 15.6984 19.9444 15.7334 19.9064 15.7767C19.8713 15.823 19.8462 15.8761 19.8328 15.9327C19.8194 15.9892 19.818 16.0481 19.8287 16.1053L20.023 17.083C20.0409 17.1714 20.0887 17.2507 20.158 17.3077C20.2275 17.3647 20.3144 17.3959 20.4039 17.3959H22.2698C22.3539 17.3959 22.4358 17.3684 22.503 17.3176C22.5703 17.2669 22.6194 17.1955 22.643 17.1142C22.6819 16.9735 22.7597 16.7154 22.9229 16.1053C22.9335 16.0481 22.9322 15.9892 22.9188 15.9327C22.9054 15.8761 22.8803 15.823 22.8452 15.7767C22.8072 15.7334 22.7605 15.6984 22.7083 15.6741C22.6562 15.6499 22.5994 15.6369 22.5419 15.636C22.4388 15.636 22.3399 15.5948 22.267 15.5215C22.1942 15.4481 22.1532 15.3486 22.1532 15.2449V11.7252C22.1532 10.2731 21.5799 8.88042 20.5593 7.85364C19.5387 6.82685 18.1544 6.25 16.7112 6.25C16.2523 6.25105 15.7954 6.30884 15.3507 6.42208C15.2229 6.45326 15.0897 6.45326 14.9619 6.42208C14.5172 6.30884 14.0602 6.25105 13.6014 6.25C12.158 6.25 10.7738 6.82685 9.75324 7.85364C8.73265 8.88042 8.1593 10.2731 8.1593 11.7252V15.2449C8.1593 15.3486 8.11834 15.4481 8.04544 15.5215C7.97254 15.5948 7.87368 15.636 7.77058 15.636C7.71308 15.6369 7.65639 15.6499 7.60417 15.6741C7.55195 15.6984 7.50535 15.7334 7.46738 15.7767C7.43223 15.823 7.40714 15.8761 7.39375 15.9327C7.38035 15.9892 7.37895 16.0481 7.38963 16.1053C7.55289 16.7154 7.63064 16.9735 7.66952 17.1142C7.69307 17.1955 7.74217 17.2669 7.80945 17.3176C7.87674 17.3684 7.95858 17.3959 8.04268 17.3959H9.90854C9.99815 17.3959 10.085 17.3647 10.1544 17.3077C10.2239 17.2507 10.2716 17.1714 10.2895 17.083L10.4916 16.1053C10.5023 16.0481 10.5009 15.9892 10.4875 15.9327C10.4741 15.8761 10.449 15.823 10.4139 15.7767C10.375 15.7324 10.3272 15.6968 10.2735 15.6725C10.2199 15.6483 10.1617 15.6357 10.1029 15.636C9.9998 15.636 9.90093 15.5948 9.82803 15.5215C9.75513 15.4481 9.71418 15.3486 9.71418 15.2449V11.7252C9.71404 10.941 9.94822 10.1749 10.3863 9.52634C10.8244 8.87776 11.4462 8.37666 12.1709 8.08809C12.2099 8.0711 12.2536 8.06805 12.2946 8.07943C12.3356 8.09081 12.3715 8.11594 12.3963 8.15066C12.4228 8.18396 12.4373 8.22534 12.4373 8.26799C12.4373 8.31064 12.4228 8.35201 12.3963 8.38531C11.6647 9.34301 11.2683 10.5172 11.2691 11.7252C11.2691 12.7624 11.6786 13.7571 12.4076 14.4905C13.1365 15.224 14.1253 15.636 15.1563 15.636C16.1872 15.636 17.1759 15.224 17.9049 14.4905C18.6339 13.7571 19.0434 12.7624 19.0434 11.7252C19.0442 10.5172 18.6478 9.34301 17.9162 8.38531C17.8897 8.35201 17.8753 8.31064 17.8753 8.26799C17.8753 8.22534 17.8897 8.18396 17.9162 8.15066C17.941 8.11594 17.9769 8.09081 18.0179 8.07943C18.0589 8.06805 18.1025 8.0711 18.1417 8.08809ZM15.1563 14.0716C14.5377 14.0716 13.9444 13.8245 13.507 13.3844C13.0697 12.9444 12.8239 12.3475 12.8239 11.7252C12.8239 11.0387 13.0035 10.3643 13.3448 9.7698C13.6859 9.1753 14.1767 8.68163 14.7675 8.33839C14.8858 8.26974 15.0198 8.23359 15.1563 8.23359C15.2928 8.23359 15.4268 8.26974 15.545 8.33839C16.1359 8.68163 16.6265 9.1753 16.9678 9.7698C17.3089 10.3643 17.4885 11.0387 17.4885 11.7252C17.4885 12.3475 17.2429 12.9444 16.8054 13.3844C16.368 13.8245 15.7748 14.0716 15.1563 14.0716Z"
            fill="currentColor"
          />
        </g>
      </svg>
    ),
    styles: [
      {
        name: 'HITT',
        value: 'hitt',
        icon: (
          <svg
            width={30}
            height={30}
            viewBox="0 0 30 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <mask
              id="mask0_736_308"
              style={{ maskType: 'luminance' }}
              maskUnits="userSpaceOnUse"
              x={5}
              y={5}
              width={20}
              height={20}
            >
              <path d="M24.375 5.625H5.625V24.375H24.375V5.625Z" fill="white" />
            </mask>
            <g mask="url(#mask0_736_308)">
              <path
                d="M21.4499 14.5419C21.2035 14.2294 20.9036 13.9585 20.6251 13.6877C19.9073 13.0627 19.0932 12.6148 18.4076 11.9585C16.8115 10.4377 16.4579 7.92711 17.4756 6C16.4579 6.23959 15.5688 6.78127 14.8082 7.37504C12.0338 9.54179 10.9411 13.3648 12.248 16.6462C12.2909 16.7503 12.3337 16.8545 12.3337 16.9899C12.3337 17.2191 12.173 17.4274 11.9588 17.5108C11.7124 17.6149 11.4553 17.5524 11.2517 17.3857C11.191 17.3362 11.1401 17.2762 11.1018 17.2087C9.89131 15.719 9.69848 13.5835 10.5126 11.8752C8.72363 13.2919 7.74886 15.6878 7.88813 17.9482C7.95233 18.4691 8.01661 18.99 8.19878 19.5108C8.34871 20.1358 8.63798 20.7608 8.95936 21.313C10.1162 23.1151 12.1195 24.4068 14.2727 24.6672C16.5651 24.9484 19.0182 24.5422 20.775 23.0005C22.7354 21.2713 23.4209 18.5004 22.414 16.1253L22.2748 15.8545C22.0498 15.3753 21.4499 14.5419 21.4499 14.5419ZM18.0648 21.1046C17.7649 21.3546 17.2721 21.6254 16.8865 21.7296C15.6866 22.1463 14.4869 21.5629 13.7799 20.8754C15.0546 20.5837 15.8152 19.6671 16.0402 18.7399C16.2223 17.9066 15.8795 17.2191 15.7403 16.417C15.6117 15.6461 15.6331 14.9899 15.9224 14.2711C16.1259 14.6669 16.3401 15.0628 16.5972 15.3753C17.4221 16.417 18.7183 16.8753 18.9968 18.292C19.0397 18.4378 19.061 18.5837 19.061 18.7399C19.0932 19.5941 18.7076 20.5317 18.0648 21.1046Z"
                fill="currentColor"
              />
            </g>
          </svg>
        ),
      },
      {
        name: 'Cardio',
        value: 'cardio',
        icon: (
          <svg
            width={30}
            height={30}
            viewBox="0 0 30 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14.7417 9.57884C13.1009 7.54244 10.1695 7.41988 8.38235 9.31297C7.13016 10.6393 6.80464 12.5754 7.40571 14.2188H10.6096L12.6558 10.1281L14.9995 14.8148L16.1714 12.4718L17.0448 14.2188H22.5933C23.1944 12.5754 22.8688 10.6393 21.6168 9.31297C19.8294 7.41992 16.8981 7.54244 15.2574 9.57884L14.9995 9.89894L14.7417 9.57884ZM21.6374 15.7813H16.2636L14.9995 18.3094L12.6558 13.6219L11.5761 15.7813H8.36165C8.36847 15.7886 8.37537 15.796 8.38235 15.8033L14.9962 22.809L14.9994 22.8125L21.6167 15.8033C21.6236 15.796 21.6305 15.7886 21.6374 15.7813Z"
              fill="currentColor"
            />
          </svg>
        ),
      },
    ],
  },
  {
    name: 'Hypertrophy',
    value: 'hypertrophy-goal',
    icon: (
      <svg
        width={30}
        height={30}
        viewBox="0 0 30 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <mask
          id="mask0_735_290"
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x={5}
          y={5}
          width={20}
          height={20}
        >
          <path d="M24.375 5.625H5.625V24.375H24.375V5.625Z" fill="white" />
        </mask>
        <g mask="url(#mask0_735_290)">
          <path
            d="M13.4643 8.98263C13.578 8.98263 13.6832 8.92136 13.7391 8.82244C13.7954 8.72359 13.7938 8.60201 13.735 8.50436L12.1269 5.82386H15.0768L15.623 10.7379L14.1962 10.2622C14.0815 10.224 13.9552 10.2546 13.8705 10.3409C13.7858 10.4271 13.7581 10.5544 13.7985 10.6681L14.5888 12.8811L11.8597 8.98263H13.4643ZM13.1759 11.9641L15.4167 15.1652C15.4777 15.2524 15.5753 15.3001 15.6754 15.3001C15.7244 15.3001 15.7739 15.2887 15.8201 15.265C15.9607 15.1924 16.0257 15.0272 15.9727 14.8781L14.6116 11.067L15.8912 11.4938C15.9935 11.5273 16.106 11.5074 16.1897 11.4398C16.2734 11.3722 16.3169 11.2664 16.3049 11.1593L16.1501 9.76593C17.4345 9.02463 18.8123 8.45891 20.2512 8.08774C20.3858 8.05264 20.5241 8.03501 20.6627 8.03501C20.9423 8.03501 21.2016 8.11143 21.4322 8.23496L16.4174 12.5334L16.8287 13.0131L21.9284 8.64206C21.9711 8.69388 22.0131 8.74541 22.0491 8.80196C19.0922 14.3035 14.6075 18.7188 9.06008 21.5892C9.02723 21.5639 8.99528 21.538 8.96468 21.5105L13.717 15.1737L13.2116 14.7947L8.87303 20.5793L9.16936 19.8016C10.1034 17.3501 11.5172 15.1668 13.3718 13.3123L12.9251 12.8657C11.0071 14.7837 9.54488 17.0415 8.57896 19.5767L8.45986 19.8891C8.46083 19.8847 8.46113 19.8803 8.4624 19.8762C9.24863 16.8296 10.8767 14.1026 13.1759 11.9641ZM6.54474 24.3594L5.98438 23.799L7.95698 21.1707C8.1879 21.7174 8.62635 22.1559 9.1731 22.3867L6.54474 24.3594ZM17.5959 20.9859L17.8591 24.1446H15.4818L16.2911 21.7174C16.3232 21.621 16.3068 21.5149 16.2475 21.4328C16.1881 21.3507 16.0927 21.3017 15.9913 21.3017H15.2777L16.623 18.9955V20.67C16.623 20.8446 16.7645 20.9859 16.9389 20.9859H17.5959ZM22.2563 10.0926C21.4332 13.2832 19.7095 16.0971 17.2548 18.27V17.8271C17.2548 17.6843 17.1591 17.5593 17.0213 17.522C16.8836 17.485 16.738 17.5447 16.666 17.6679L15.5408 19.5966C13.9896 20.6431 12.2836 21.4126 10.4676 21.8811C10.3334 21.9158 10.195 21.9335 10.0563 21.9335C9.96946 21.9335 9.88606 21.9208 9.80296 21.9079C15.0731 19.0732 19.3718 14.8557 22.3047 9.64218C22.3053 9.65538 22.3088 9.66776 22.3088 9.68103C22.3088 9.81963 22.2911 9.95801 22.2563 10.0926ZM22.7621 8.79783C22.5311 8.25101 22.0928 7.81263 21.5459 7.58171L24.1743 5.60938L24.7344 6.16942L22.7621 8.79783Z"
            fill="currentColor"
          />
        </g>
      </svg>
    ),
    styles: [
      {
        name: 'Weight training',
        value: 'weight-training',
        icon: (
          <svg
            width={30}
            height={30}
            viewBox="0 0 30 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.77502 9.57301C9.97459 9.45309 10.282 9.33594 10.7031 9.33594C11.1242 9.33594 11.4316 9.45309 11.6313 9.57301C11.9394 9.75736 12.0316 10.073 12.0363 10.298C12.0453 10.7465 12.0703 12.2254 12.0703 14.9999C12.0703 17.7746 12.0453 19.2535 12.0363 19.7019C12.0316 19.9265 11.9394 20.2425 11.6313 20.4269C11.4316 20.5469 11.1242 20.664 10.7031 20.664C10.282 20.664 9.97459 20.5469 9.77502 20.4269C9.46677 20.2425 9.37459 19.9269 9.36994 19.7019C9.36094 19.2539 9.33597 17.7746 9.33597 14.9999C9.33597 12.2254 9.36094 10.7465 9.36994 10.298C9.37459 10.0734 9.46677 9.75736 9.77502 9.57301ZM20.225 9.57301C20.0254 9.45309 19.718 9.33594 19.2969 9.33594C18.8758 9.33594 18.5684 9.45309 18.3687 9.57301C18.0609 9.75736 17.9684 10.073 17.9637 10.298C17.9547 10.7461 17.9297 12.2254 17.9297 14.9999C17.9297 17.7746 17.9547 19.2535 17.9637 19.7019C17.9684 19.9265 18.0606 20.2425 18.3687 20.4269C18.5684 20.5469 18.8758 20.664 19.2969 20.664C19.718 20.664 20.0254 20.5469 20.225 20.4269C20.5328 20.2425 20.6254 19.9269 20.6301 19.7019C20.6391 19.2535 20.664 17.7746 20.664 14.9999C20.664 12.2254 20.6391 10.7465 20.6301 10.298C20.6254 10.0734 20.5328 9.75736 20.225 9.57301ZM7.38281 11.289C7.125 11.289 6.91836 11.3359 6.76016 11.3972C6.36328 11.5511 6.24063 11.9269 6.23476 12.1785C6.22696 12.5288 6.21094 13.4273 6.21094 14.9999C6.21094 16.5726 6.22657 17.471 6.23438 17.8214C6.24024 18.073 6.36328 18.4492 6.76016 18.6027C6.91836 18.6641 7.125 18.7109 7.38281 18.7109C7.6406 18.7109 7.84729 18.6641 8.00547 18.6027C8.40237 18.4492 8.52499 18.073 8.53084 17.8214C8.53864 17.471 8.55469 16.5726 8.55469 14.9999C8.55469 13.4273 8.53909 12.5288 8.53122 12.1785C8.52537 11.9269 8.40237 11.5508 8.00547 11.3972C7.80642 11.323 7.59529 11.2863 7.38281 11.289ZM23.2398 11.3972C23.0408 11.323 22.8297 11.2863 22.6172 11.289C22.3593 11.289 22.1527 11.3359 21.9945 11.3972C21.598 11.5511 21.475 11.9269 21.4692 12.1785C21.4613 12.5288 21.4453 13.4273 21.4453 14.9999C21.4453 16.5726 21.4609 17.471 21.4687 17.8214C21.4746 18.073 21.5976 18.4492 21.9945 18.6027C22.1527 18.6641 22.3598 18.7109 22.6172 18.7109C22.875 18.7109 23.0817 18.6641 23.2398 18.6027C23.6363 18.4492 23.7594 18.073 23.7652 17.8214C23.773 17.471 23.7891 16.5726 23.7891 14.9999C23.7891 13.4273 23.7735 12.5288 23.7656 12.1785C23.7597 11.9269 23.6367 11.5508 23.2398 11.3972ZM12.8516 16.116V13.8839C12.8516 13.5137 13.1547 13.3191 13.4778 13.2914C13.5444 13.2851 13.644 13.2784 13.7766 13.271C14.0422 13.2562 14.4434 13.2422 15 13.2422C15.5566 13.2422 15.9582 13.2562 16.2234 13.271C16.356 13.2784 16.4556 13.2851 16.5222 13.2914C16.8453 13.3187 17.1484 13.5137 17.1484 13.8839V16.116C17.1484 16.4867 16.8453 16.6808 16.5222 16.7086C16.4556 16.7148 16.356 16.7216 16.2234 16.7288C15.9578 16.7437 15.5566 16.7578 15 16.7578C14.4434 16.7578 14.0418 16.7437 13.7766 16.7288C13.644 16.7216 13.5444 16.7148 13.4778 16.7086C13.1547 16.6812 12.8516 16.4863 12.8516 16.116Z"
              fill="currentColor"
            />
          </svg>
        ),
      },
      {
        name: 'Bodybuilding Split',
        value: 'body-building-split',
        icon: (
          <svg
            width={30}
            height={30}
            viewBox="0 0 30 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M23.5631 17.0211C23.5521 17.0055 23.5435 16.9883 23.5377 16.9704C23.5193 16.9144 21.6311 11.3194 17.0916 9.16189C17.0655 9.14824 16.5983 8.89999 16.284 8.36951C16.2703 8.34446 16.071 7.99339 15.3981 7.89844C15.4173 7.94689 15.4353 7.99534 15.4517 8.04424C15.4759 8.11579 15.4971 8.18846 15.517 8.26234C15.531 8.31439 15.5455 8.36636 15.5572 8.41871C15.5748 8.49731 15.587 8.57786 15.5991 8.65841C15.6061 8.70529 15.6159 8.75141 15.6206 8.79874C15.6347 8.92699 15.6429 9.05674 15.6429 9.18851C15.6429 9.55631 15.5865 9.91324 15.4794 10.2534C15.8763 10.3053 16.2336 10.5141 16.4638 10.8433C16.9404 11.5227 17.2531 12.5559 17.3934 13.9144C17.3946 13.941 17.4126 14.8479 17.8641 15.6549C18.1444 15.9574 18.4192 16.3097 18.6812 16.7331C18.7378 16.8249 18.7097 16.9453 18.6178 17.002C18.5256 17.0591 18.4056 17.031 18.3485 16.9386C16.612 14.1349 14.3418 14.3953 13.1424 14.7999C12.6288 14.9735 12.0744 14.9301 11.621 14.6814C9.23043 13.3703 7.10494 14.7295 6.5033 15.183C6.5033 15.183 6.47554 19.6631 6.28516 22.6236L8.99628 23.9985C9.26718 23.8612 10.1628 23.3409 10.4799 22.3167C10.5537 22.0813 10.6089 21.8675 10.6573 21.6787C10.7879 21.1725 10.8821 20.8065 11.1843 20.658C11.4075 20.5493 11.6867 20.5809 12.1182 20.7659C12.2379 20.8171 12.3575 20.8675 12.481 20.9085L13.259 21.1662C13.4126 21.2174 13.5428 21.2526 13.6691 21.2772C15.5369 21.6466 17.5569 21.3999 19.0717 20.6177C20.4935 19.8851 21.2863 19.5591 21.7007 19.4145C22.2629 19.2205 22.7628 18.9148 23.1475 18.5305C23.7253 17.9617 23.8645 17.4547 23.5631 17.0211ZM21.8563 17.1627C21.723 17.7756 21.2367 18.2956 20.4111 18.7084C20.3829 18.7225 20.3532 18.7291 20.3238 18.7291C20.2519 18.7291 20.1831 18.6897 20.1487 18.6208C20.1006 18.5243 20.1397 18.407 20.2363 18.3589C20.949 18.0024 21.3653 17.5731 21.4736 17.0833C21.6123 16.4562 21.1925 15.9191 21.1886 15.9136C21.1214 15.8292 21.135 15.7065 21.2191 15.6392C21.3031 15.5716 21.4259 15.5853 21.4931 15.6689C21.515 15.6967 22.0334 16.3511 21.8563 17.1627Z"
              fill="currentColor"
            />
            <path
              d="M9.26444 10.7459C9.27577 10.7658 9.28394 10.7885 9.29572 10.8084C9.35549 10.9116 9.42547 11.0063 9.49582 11.1009C9.52124 11.1348 9.54277 11.1728 9.56932 11.206C9.62677 11.2768 9.69172 11.3394 9.75427 11.4046C9.80152 11.4535 9.84494 11.5071 9.89534 11.5527C9.94072 11.5946 9.99232 11.629 10.0404 11.6681C10.1143 11.7283 10.1862 11.7909 10.2648 11.8444C10.2953 11.8647 10.3289 11.8796 10.3602 11.8992C10.4614 11.9621 10.5634 12.025 10.6714 12.0766C10.6909 12.0856 10.712 12.0915 10.732 12.1001C10.8543 12.1556 10.979 12.2068 11.1084 12.2467C11.1377 12.2556 11.1698 12.2596 11.1999 12.2678C11.3215 12.301 11.4439 12.3327 11.5701 12.3514C11.728 12.3753 11.8895 12.3878 12.0533 12.3878C12.2171 12.3878 12.3781 12.3753 12.5361 12.3514C12.6624 12.3327 12.7843 12.3014 12.9055 12.2678C12.9356 12.2596 12.9681 12.2561 12.9982 12.2467C13.1291 12.2064 13.2554 12.1548 13.3793 12.0985C13.3973 12.0903 13.4169 12.0852 13.4348 12.0766C13.5475 12.0227 13.6542 11.9582 13.7593 11.8917C13.7859 11.8749 13.8148 11.8624 13.841 11.8448C13.9251 11.7881 14.0017 11.7216 14.0803 11.6575C14.1229 11.622 14.1694 11.5915 14.2105 11.5536C14.2651 11.5039 14.3125 11.4461 14.3637 11.3921C14.4216 11.3311 14.4829 11.2733 14.5361 11.2072C14.5658 11.1705 14.59 11.1282 14.6182 11.0899C14.6851 10.9992 14.7526 10.9089 14.8102 10.81C14.8227 10.7885 14.8316 10.7643 14.8438 10.7427C14.9106 10.6216 14.9743 10.4973 15.0263 10.3655C15.1756 9.99573 15.2511 9.60048 15.2511 9.19C15.2511 9.07308 15.2437 8.95781 15.2311 8.84321C15.2265 8.80023 15.2182 8.75838 15.2116 8.71616C15.2011 8.64543 15.1901 8.57463 15.1749 8.50511C15.1643 8.45741 15.151 8.41046 15.1385 8.36396C15.121 8.29946 15.1025 8.23533 15.0811 8.17203C15.0658 8.12665 15.049 8.08248 15.0318 8.03831C15.0064 7.97306 14.9786 7.90893 14.9489 7.84518C14.9305 7.80648 14.913 7.76815 14.8934 7.73065C14.8336 7.61493 14.7683 7.50348 14.6968 7.39797C14.6776 7.36943 14.6554 7.34362 14.6354 7.31587C14.5807 7.2412 14.5232 7.16927 14.4634 7.10008C14.4266 7.05825 14.3895 7.01642 14.3508 6.97655C14.2984 6.9222 14.2429 6.87099 14.187 6.82095C14.1186 6.75958 14.0474 6.70093 13.9739 6.64543C13.9184 6.60399 13.8629 6.56216 13.8046 6.52424C13.7519 6.48984 13.6975 6.45739 13.6424 6.42572C13.5823 6.39132 13.5205 6.35926 13.4579 6.32878C13.4086 6.30453 13.3602 6.27913 13.3097 6.25763C13.201 6.2111 13.0893 6.17045 12.9751 6.13682C12.9349 6.12431 12.8934 6.11572 12.8527 6.10516C12.7612 6.08171 12.6686 6.06177 12.5748 6.04652C12.5345 6.03988 12.4942 6.03284 12.4536 6.02776C12.3219 6.01173 12.1885 6 12.0533 6C11.9176 6 11.7847 6.01173 11.653 6.02776C11.6119 6.03284 11.572 6.03988 11.5318 6.04652C11.4376 6.06177 11.3453 6.08171 11.2538 6.10516C11.2128 6.11572 11.1714 6.12471 11.1315 6.13682C11.0173 6.17045 10.9055 6.2111 10.7968 6.25763C10.7461 6.27913 10.6975 6.30453 10.6483 6.32878C10.5857 6.35926 10.5244 6.39132 10.4638 6.42611C10.409 6.45739 10.3547 6.48944 10.3019 6.52424C10.2441 6.56216 10.1886 6.60359 10.1331 6.64543C10.0591 6.70093 9.98722 6.75997 9.91844 6.82174C9.86294 6.87177 9.80782 6.9222 9.75577 6.97615C9.71707 7.01641 9.67957 7.05825 9.64282 7.10047C9.58342 7.16927 9.52672 7.24003 9.47279 7.31391C9.45164 7.34245 9.42899 7.36943 9.40904 7.39875C9.33787 7.50348 9.27382 7.61373 9.21397 7.72833C9.19364 7.76815 9.17452 7.80926 9.15532 7.85028C9.12682 7.91126 9.10019 7.97261 9.07559 8.03478C9.05797 8.08091 9.04042 8.12741 9.02474 8.17436C9.00367 8.23616 8.98567 8.29826 8.96887 8.36081C8.95597 8.40933 8.94224 8.45703 8.93137 8.50623C8.91652 8.57463 8.90557 8.64461 8.89499 8.71421C8.88832 8.75726 8.88014 8.79986 8.87504 8.84321C8.86289 8.95781 8.85547 9.07308 8.85547 9.19C8.85547 9.60085 8.93092 9.99573 9.07949 10.3635C9.13229 10.4973 9.19642 10.6231 9.26444 10.7459ZM11.7054 7.60278C11.7171 7.60046 11.7292 7.59731 11.7409 7.59536C11.8406 7.57586 11.9434 7.56453 12.0486 7.56408C12.1537 7.56453 12.2566 7.57586 12.3562 7.59536C12.3684 7.59731 12.3801 7.60046 12.3919 7.60278C13.126 7.76231 13.678 8.4124 13.678 9.19C13.678 9.8647 13.2671 10.4453 12.6823 10.6943C12.6799 10.695 12.6776 10.6962 12.6753 10.6971C12.5866 10.7342 12.4935 10.7623 12.3977 10.7834C12.3844 10.7862 12.3715 10.7901 12.3578 10.7924C12.2578 10.812 12.1546 10.8233 12.0486 10.8237C11.9427 10.8233 11.8399 10.812 11.7394 10.7924C11.7261 10.7901 11.7132 10.7862 11.6995 10.7834C11.6041 10.7623 11.5107 10.7342 11.422 10.6971C11.42 10.6962 11.4176 10.695 11.4153 10.6943C10.8304 10.4453 10.4192 9.8647 10.4192 9.19C10.4192 8.4124 10.9712 7.76231 11.7054 7.60278Z"
              fill="currentColor"
            />
          </svg>
        ),
      },
    ],
  },
  {
    name: 'Performance',
    value: 'performance',
    icon: (
      <svg
        width={30}
        height={30}
        viewBox="0 0 30 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <mask
          id="mask0_708_356"
          style={{ maskType: 'luminance' }}
          maskUnits="userSpaceOnUse"
          x={5}
          y={5}
          width={20}
          height={20}
        >
          <path d="M24.375 5.625H5.625V24.375H24.375V5.625Z" fill="white" />
        </mask>
        <g mask="url(#mask0_708_356)">
          <path
            d="M7.1877 19.5836C6.9805 19.5836 6.78178 19.6659 6.63527 19.8124C6.48876 19.9589 6.40645 20.1576 6.40645 20.3649V21.5368C6.40645 21.5885 6.42703 21.6383 6.46366 21.6749C6.50028 21.7115 6.54996 21.732 6.60176 21.732H9.33613C9.38793 21.732 9.43762 21.7115 9.47425 21.6749C9.51087 21.6383 9.53145 21.5885 9.53145 21.5368V20.3649C9.53145 20.1576 9.44913 19.9589 9.30262 19.8124C9.15611 19.6659 8.9574 19.5836 8.7502 19.5836H7.1877ZM11.8752 17.2399C11.668 17.2399 11.4693 17.3221 11.3228 17.4686C11.1763 17.6151 11.0939 17.8139 11.0939 18.0211V21.5368C11.0939 21.5885 11.1145 21.6383 11.1512 21.6749C11.1878 21.7115 11.2375 21.732 11.2893 21.732H14.0236C14.0754 21.732 14.1251 21.7115 14.1618 21.6749C14.1984 21.6383 14.219 21.5885 14.219 21.5368V18.0211C14.219 17.8139 14.1366 17.6151 13.9901 17.4686C13.8436 17.3221 13.6449 17.2399 13.4378 17.2399H11.8752ZM16.5628 14.5055C16.3555 14.5055 16.1568 14.5878 16.0103 14.7343C15.8638 14.8808 15.7815 15.0795 15.7815 15.2868V21.5368C15.7815 21.5885 15.802 21.6383 15.8386 21.6749C15.8753 21.7115 15.925 21.732 15.9768 21.732H18.7111C18.7629 21.732 18.8126 21.7115 18.8493 21.6749C18.8859 21.6383 18.9065 21.5885 18.9065 21.5368V15.2868C18.9065 15.0795 18.8241 14.8808 18.6776 14.7343C18.5311 14.5878 18.3324 14.5055 18.1253 14.5055H16.5628ZM20.6643 21.732H23.3986C23.4504 21.732 23.5001 21.7115 23.5368 21.6749C23.5734 21.6383 23.594 21.5885 23.594 21.5368V12.1617C23.594 11.9545 23.5116 11.7558 23.3651 11.6093C23.2186 11.4628 23.0199 11.3805 22.8128 11.3805H21.2503C21.043 11.3805 20.8443 11.4628 20.6978 11.6093C20.5513 11.7558 20.469 11.9545 20.469 12.1617V21.5368C20.469 21.5885 20.4895 21.6383 20.5261 21.6749C20.5628 21.7115 20.6125 21.732 20.6643 21.732ZM19.7424 8.91174L19.9846 9.27111C20.0376 9.35085 20.1096 9.41615 20.1943 9.46115C20.2788 9.50615 20.3731 9.52944 20.469 9.52893C20.5684 9.52533 20.6653 9.4961 20.7501 9.44409C20.835 9.39207 20.905 9.31904 20.9534 9.23205L22.5159 6.49768C22.5733 6.40499 22.6036 6.29811 22.6036 6.18908C22.6036 6.08005 22.5733 5.97318 22.5159 5.88049C22.4581 5.78858 22.375 5.71534 22.2766 5.6696C22.1781 5.62385 22.0686 5.60755 21.9611 5.62268L18.8361 6.0133C18.7371 6.02583 18.643 6.06343 18.5625 6.12254C18.4821 6.18166 18.4181 6.26034 18.3768 6.3511C18.3353 6.44188 18.3176 6.54174 18.3256 6.64121C18.3335 6.7407 18.3668 6.8365 18.4221 6.91955L18.6565 7.27893C18.6709 7.29976 18.6809 7.32333 18.686 7.34816C18.691 7.373 18.691 7.3986 18.6859 7.42344C18.6808 7.44826 18.6708 7.4718 18.6563 7.49261C18.6418 7.51343 18.6233 7.5311 18.6018 7.54455L7.53926 14.9351C7.45338 14.9919 7.37957 15.0651 7.32206 15.1505C7.26455 15.2359 7.22447 15.3318 7.20415 15.4326C7.18382 15.5336 7.18363 15.6375 7.20361 15.7385C7.22357 15.8395 7.26331 15.9355 7.32051 16.0211C7.39217 16.1271 7.48878 16.2139 7.60186 16.2739C7.71492 16.3338 7.84098 16.365 7.96895 16.3649C8.12255 16.3666 8.27282 16.3201 8.39863 16.232L19.469 8.85705C19.4904 8.84244 19.5146 8.83226 19.54 8.8271C19.5655 8.82194 19.5916 8.8219 19.6171 8.82699C19.6426 8.83209 19.6668 8.8422 19.6883 8.85675C19.7098 8.8713 19.7281 8.89 19.7424 8.91174ZM6.40645 24.3726H23.594C23.7796 24.3723 23.959 24.3058 24.1001 24.185C24.2411 24.0644 24.3346 23.8974 24.3638 23.7141C24.3929 23.5308 24.3558 23.343 24.2591 23.1845C24.1625 23.026 24.0125 22.9071 23.8361 22.8493C23.758 22.8235 23.6763 22.8104 23.594 22.8101H6.40645C6.32417 22.8104 6.24243 22.8235 6.16426 22.8493C5.98791 22.9071 5.83795 23.026 5.74127 23.1845C5.64458 23.343 5.6075 23.5308 5.63665 23.7141C5.66578 23.8974 5.75926 24.0644 5.90032 24.185C6.04138 24.3058 6.22081 24.3723 6.40645 24.3726Z"
            fill="currentColor"
          />
        </g>
      </svg>
    ),
    styles: [
      {
        name: 'HITT',
        value: 'hitt',
        icon: (
          <svg
            width={30}
            height={30}
            viewBox="0 0 30 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <mask
              id="mask0_736_308"
              style={{ maskType: 'luminance' }}
              maskUnits="userSpaceOnUse"
              x={5}
              y={5}
              width={20}
              height={20}
            >
              <path d="M24.375 5.625H5.625V24.375H24.375V5.625Z" fill="white" />
            </mask>
            <g mask="url(#mask0_736_308)">
              <path
                d="M21.4499 14.5419C21.2035 14.2294 20.9036 13.9585 20.6251 13.6877C19.9073 13.0627 19.0932 12.6148 18.4076 11.9585C16.8115 10.4377 16.4579 7.92711 17.4756 6C16.4579 6.23959 15.5688 6.78127 14.8082 7.37504C12.0338 9.54179 10.9411 13.3648 12.248 16.6462C12.2909 16.7503 12.3337 16.8545 12.3337 16.9899C12.3337 17.2191 12.173 17.4274 11.9588 17.5108C11.7124 17.6149 11.4553 17.5524 11.2517 17.3857C11.191 17.3362 11.1401 17.2762 11.1018 17.2087C9.89131 15.719 9.69848 13.5835 10.5126 11.8752C8.72363 13.2919 7.74886 15.6878 7.88813 17.9482C7.95233 18.4691 8.01661 18.99 8.19878 19.5108C8.34871 20.1358 8.63798 20.7608 8.95936 21.313C10.1162 23.1151 12.1195 24.4068 14.2727 24.6672C16.5651 24.9484 19.0182 24.5422 20.775 23.0005C22.7354 21.2713 23.4209 18.5004 22.414 16.1253L22.2748 15.8545C22.0498 15.3753 21.4499 14.5419 21.4499 14.5419ZM18.0648 21.1046C17.7649 21.3546 17.2721 21.6254 16.8865 21.7296C15.6866 22.1463 14.4869 21.5629 13.7799 20.8754C15.0546 20.5837 15.8152 19.6671 16.0402 18.7399C16.2223 17.9066 15.8795 17.2191 15.7403 16.417C15.6117 15.6461 15.6331 14.9899 15.9224 14.2711C16.1259 14.6669 16.3401 15.0628 16.5972 15.3753C17.4221 16.417 18.7183 16.8753 18.9968 18.292C19.0397 18.4378 19.061 18.5837 19.061 18.7399C19.0932 19.5941 18.7076 20.5317 18.0648 21.1046Z"
                fill="currentColor"
              />
            </g>
          </svg>
        ),
      },
      {
        name: 'Bodybuilding Split',
        value: 'body-building-split',
        icon: (
          <svg
            width={30}
            height={30}
            viewBox="0 0 30 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M23.5631 17.0211C23.5521 17.0055 23.5435 16.9883 23.5377 16.9704C23.5193 16.9144 21.6311 11.3194 17.0916 9.16189C17.0655 9.14824 16.5983 8.89999 16.284 8.36951C16.2703 8.34446 16.071 7.99339 15.3981 7.89844C15.4173 7.94689 15.4353 7.99534 15.4517 8.04424C15.4759 8.11579 15.4971 8.18846 15.517 8.26234C15.531 8.31439 15.5455 8.36636 15.5572 8.41871C15.5748 8.49731 15.587 8.57786 15.5991 8.65841C15.6061 8.70529 15.6159 8.75141 15.6206 8.79874C15.6347 8.92699 15.6429 9.05674 15.6429 9.18851C15.6429 9.55631 15.5865 9.91324 15.4794 10.2534C15.8763 10.3053 16.2336 10.5141 16.4638 10.8433C16.9404 11.5227 17.2531 12.5559 17.3934 13.9144C17.3946 13.941 17.4126 14.8479 17.8641 15.6549C18.1444 15.9574 18.4192 16.3097 18.6812 16.7331C18.7378 16.8249 18.7097 16.9453 18.6178 17.002C18.5256 17.0591 18.4056 17.031 18.3485 16.9386C16.612 14.1349 14.3418 14.3953 13.1424 14.7999C12.6288 14.9735 12.0744 14.9301 11.621 14.6814C9.23043 13.3703 7.10494 14.7295 6.5033 15.183C6.5033 15.183 6.47554 19.6631 6.28516 22.6236L8.99628 23.9985C9.26718 23.8612 10.1628 23.3409 10.4799 22.3167C10.5537 22.0813 10.6089 21.8675 10.6573 21.6787C10.7879 21.1725 10.8821 20.8065 11.1843 20.658C11.4075 20.5493 11.6867 20.5809 12.1182 20.7659C12.2379 20.8171 12.3575 20.8675 12.481 20.9085L13.259 21.1662C13.4126 21.2174 13.5428 21.2526 13.6691 21.2772C15.5369 21.6466 17.5569 21.3999 19.0717 20.6177C20.4935 19.8851 21.2863 19.5591 21.7007 19.4145C22.2629 19.2205 22.7628 18.9148 23.1475 18.5305C23.7253 17.9617 23.8645 17.4547 23.5631 17.0211ZM21.8563 17.1627C21.723 17.7756 21.2367 18.2956 20.4111 18.7084C20.3829 18.7225 20.3532 18.7291 20.3238 18.7291C20.2519 18.7291 20.1831 18.6897 20.1487 18.6208C20.1006 18.5243 20.1397 18.407 20.2363 18.3589C20.949 18.0024 21.3653 17.5731 21.4736 17.0833C21.6123 16.4562 21.1925 15.9191 21.1886 15.9136C21.1214 15.8292 21.135 15.7065 21.2191 15.6392C21.3031 15.5716 21.4259 15.5853 21.4931 15.6689C21.515 15.6967 22.0334 16.3511 21.8563 17.1627Z"
              fill="currentColor"
            />
            <path
              d="M9.26444 10.7459C9.27577 10.7658 9.28394 10.7885 9.29572 10.8084C9.35549 10.9116 9.42547 11.0063 9.49582 11.1009C9.52124 11.1348 9.54277 11.1728 9.56932 11.206C9.62677 11.2768 9.69172 11.3394 9.75427 11.4046C9.80152 11.4535 9.84494 11.5071 9.89534 11.5527C9.94072 11.5946 9.99232 11.629 10.0404 11.6681C10.1143 11.7283 10.1862 11.7909 10.2648 11.8444C10.2953 11.8647 10.3289 11.8796 10.3602 11.8992C10.4614 11.9621 10.5634 12.025 10.6714 12.0766C10.6909 12.0856 10.712 12.0915 10.732 12.1001C10.8543 12.1556 10.979 12.2068 11.1084 12.2467C11.1377 12.2556 11.1698 12.2596 11.1999 12.2678C11.3215 12.301 11.4439 12.3327 11.5701 12.3514C11.728 12.3753 11.8895 12.3878 12.0533 12.3878C12.2171 12.3878 12.3781 12.3753 12.5361 12.3514C12.6624 12.3327 12.7843 12.3014 12.9055 12.2678C12.9356 12.2596 12.9681 12.2561 12.9982 12.2467C13.1291 12.2064 13.2554 12.1548 13.3793 12.0985C13.3973 12.0903 13.4169 12.0852 13.4348 12.0766C13.5475 12.0227 13.6542 11.9582 13.7593 11.8917C13.7859 11.8749 13.8148 11.8624 13.841 11.8448C13.9251 11.7881 14.0017 11.7216 14.0803 11.6575C14.1229 11.622 14.1694 11.5915 14.2105 11.5536C14.2651 11.5039 14.3125 11.4461 14.3637 11.3921C14.4216 11.3311 14.4829 11.2733 14.5361 11.2072C14.5658 11.1705 14.59 11.1282 14.6182 11.0899C14.6851 10.9992 14.7526 10.9089 14.8102 10.81C14.8227 10.7885 14.8316 10.7643 14.8438 10.7427C14.9106 10.6216 14.9743 10.4973 15.0263 10.3655C15.1756 9.99573 15.2511 9.60048 15.2511 9.19C15.2511 9.07308 15.2437 8.95781 15.2311 8.84321C15.2265 8.80023 15.2182 8.75838 15.2116 8.71616C15.2011 8.64543 15.1901 8.57463 15.1749 8.50511C15.1643 8.45741 15.151 8.41046 15.1385 8.36396C15.121 8.29946 15.1025 8.23533 15.0811 8.17203C15.0658 8.12665 15.049 8.08248 15.0318 8.03831C15.0064 7.97306 14.9786 7.90893 14.9489 7.84518C14.9305 7.80648 14.913 7.76815 14.8934 7.73065C14.8336 7.61493 14.7683 7.50348 14.6968 7.39797C14.6776 7.36943 14.6554 7.34362 14.6354 7.31587C14.5807 7.2412 14.5232 7.16927 14.4634 7.10008C14.4266 7.05825 14.3895 7.01642 14.3508 6.97655C14.2984 6.9222 14.2429 6.87099 14.187 6.82095C14.1186 6.75958 14.0474 6.70093 13.9739 6.64543C13.9184 6.60399 13.8629 6.56216 13.8046 6.52424C13.7519 6.48984 13.6975 6.45739 13.6424 6.42572C13.5823 6.39132 13.5205 6.35926 13.4579 6.32878C13.4086 6.30453 13.3602 6.27913 13.3097 6.25763C13.201 6.2111 13.0893 6.17045 12.9751 6.13682C12.9349 6.12431 12.8934 6.11572 12.8527 6.10516C12.7612 6.08171 12.6686 6.06177 12.5748 6.04652C12.5345 6.03988 12.4942 6.03284 12.4536 6.02776C12.3219 6.01173 12.1885 6 12.0533 6C11.9176 6 11.7847 6.01173 11.653 6.02776C11.6119 6.03284 11.572 6.03988 11.5318 6.04652C11.4376 6.06177 11.3453 6.08171 11.2538 6.10516C11.2128 6.11572 11.1714 6.12471 11.1315 6.13682C11.0173 6.17045 10.9055 6.2111 10.7968 6.25763C10.7461 6.27913 10.6975 6.30453 10.6483 6.32878C10.5857 6.35926 10.5244 6.39132 10.4638 6.42611C10.409 6.45739 10.3547 6.48944 10.3019 6.52424C10.2441 6.56216 10.1886 6.60359 10.1331 6.64543C10.0591 6.70093 9.98722 6.75997 9.91844 6.82174C9.86294 6.87177 9.80782 6.9222 9.75577 6.97615C9.71707 7.01641 9.67957 7.05825 9.64282 7.10047C9.58342 7.16927 9.52672 7.24003 9.47279 7.31391C9.45164 7.34245 9.42899 7.36943 9.40904 7.39875C9.33787 7.50348 9.27382 7.61373 9.21397 7.72833C9.19364 7.76815 9.17452 7.80926 9.15532 7.85028C9.12682 7.91126 9.10019 7.97261 9.07559 8.03478C9.05797 8.08091 9.04042 8.12741 9.02474 8.17436C9.00367 8.23616 8.98567 8.29826 8.96887 8.36081C8.95597 8.40933 8.94224 8.45703 8.93137 8.50623C8.91652 8.57463 8.90557 8.64461 8.89499 8.71421C8.88832 8.75726 8.88014 8.79986 8.87504 8.84321C8.86289 8.95781 8.85547 9.07308 8.85547 9.19C8.85547 9.60085 8.93092 9.99573 9.07949 10.3635C9.13229 10.4973 9.19642 10.6231 9.26444 10.7459ZM11.7054 7.60278C11.7171 7.60046 11.7292 7.59731 11.7409 7.59536C11.8406 7.57586 11.9434 7.56453 12.0486 7.56408C12.1537 7.56453 12.2566 7.57586 12.3562 7.59536C12.3684 7.59731 12.3801 7.60046 12.3919 7.60278C13.126 7.76231 13.678 8.4124 13.678 9.19C13.678 9.8647 13.2671 10.4453 12.6823 10.6943C12.6799 10.695 12.6776 10.6962 12.6753 10.6971C12.5866 10.7342 12.4935 10.7623 12.3977 10.7834C12.3844 10.7862 12.3715 10.7901 12.3578 10.7924C12.2578 10.812 12.1546 10.8233 12.0486 10.8237C11.9427 10.8233 11.8399 10.812 11.7394 10.7924C11.7261 10.7901 11.7132 10.7862 11.6995 10.7834C11.6041 10.7623 11.5107 10.7342 11.422 10.6971C11.42 10.6962 11.4176 10.695 11.4153 10.6943C10.8304 10.4453 10.4192 9.8647 10.4192 9.19C10.4192 8.4124 10.9712 7.76231 11.7054 7.60278Z"
              fill="currentColor"
            />
          </svg>
        ),
      },
    ],
  },
];

export type Limitation = {
  name: string;
  value: string;
  image: any;
};

export const traniningLimitation: Limitation[] = [
  { name: 'Arms', value: 'arms', image: Arms },
  { name: 'Shoulders', value: 'shoulders', image: Shoulders },
  { name: 'Chest', value: 'chest', image: Chest },
  { name: 'Core', value: 'core', image: Core },
  { name: 'Back', value: 'back', image: Back },
  { name: 'Glutes', value: 'glutes', image: Glutes },
  { name: 'Legs', value: 'legs', image: Legs },
];

export type EnergyLevel = {
  name: string;
  value: string;
  icon: ReactNode;
};

export const trainingEnergyLevel: EnergyLevel[] = [
  {
    name: 'Often low',
    value: 'often_low',
    icon: (
      <svg
        width={40}
        height={40}
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_9069_48936)">
          <mask
            id="mask0_9069_48936"
            style={{ maskType: 'luminance' }}
            maskUnits="userSpaceOnUse"
            x={5}
            y={10}
            width={26}
            height={19}
          >
            <path
              d="M26.625 27.6667C30.2917 24.0105 30.2917 18.073 26.625 14.4167C22.9688 10.7501 17.0312 10.7501 13.375 14.4167C9.70833 18.073 9.70833 24.0105 13.375 27.6667"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M17.7988 25.7621C20.4058 26.9778 23.5047 25.8499 24.7203 23.2429C25.936 20.6359 24.8081 17.5371 22.2011 16.3214C19.5941 15.1058 5.8389 14.4384 5.8389 14.4384C5.8389 14.4384 15.1919 24.5465 17.7988 25.7621Z"
              fill="black"
            />
            <path
              d="M18.679 23.8741C20.2431 24.6035 22.1024 23.9268 22.8319 22.3626C23.5613 20.7984 22.8845 18.9391 21.3203 18.2097C19.7561 17.4803 9.61486 16.1994 9.61486 16.1994C9.61486 16.1994 17.1147 23.1447 18.679 23.8741Z"
              fill="white"
            />
          </mask>
          <g mask="url(#mask0_9069_48936)">
            <path d="M7.5 7.5H32.5V32.5H7.5V7.5Z" fill="currentColor" />
          </g>
        </g>
        <defs>
          <clipPath id="clip0_9069_48936">
            <rect width={25} height={25} fill="white" transform="matrix(-1 0 0 1 32.5 7.5)" />
          </clipPath>
        </defs>
      </svg>
    ),
  },
  {
    name: 'Fluctuates',
    value: 'fluctuates',
    icon: (
      <svg
        width={40}
        height={40}
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M13.0639 19.1319C13.4493 19.1319 13.7861 18.8819 13.9016 18.5112L15.2965 13.8567C15.5327 13.1128 16.1993 12.6214 16.9615 12.6214C17.7236 12.6214 18.3903 13.1128 18.6221 13.8437L22.1681 25.644L24.3894 20.6701C24.8044 19.736 25.7393 19.1319 26.7705 19.1319H30.4163V12.1874C30.4163 10.7517 29.2479 9.58325 27.8122 9.58325H12.1872C10.7514 9.58325 9.58301 10.7517 9.58301 12.1874V19.1319H13.0639Z"
          fill="currentColor"
        />
        <path
          d="M26.7705 20.8681C26.425 20.8681 26.1125 21.0678 25.9754 21.3768L23.7523 26.3516C23.4867 26.9714 22.8634 27.3785 22.1698 27.3785C21.4077 27.3785 20.741 26.8872 20.5092 26.1563L16.9632 14.356L15.5622 19.0174C15.2184 20.1268 14.2158 20.8681 13.0639 20.8681H9.58301V27.8126C9.58301 29.2483 10.7514 30.4167 12.1872 30.4167H27.8122C29.2479 30.4167 30.4163 29.2483 30.4163 27.8126V20.8681H26.7705Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  {
    name: 'Often high',
    value: 'often_high',
    icon: (
      <svg
        width={40}
        height={40}
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_9069_48948)">
          <mask
            id="mask0_9069_48948"
            style={{ maskType: 'luminance' }}
            maskUnits="userSpaceOnUse"
            x={9}
            y={10}
            width={26}
            height={19}
          >
            <path
              d="M13.375 27.6667C9.70833 24.0105 9.70833 18.073 13.375 14.4167C17.0312 10.7501 22.9688 10.7501 26.625 14.4167C30.2917 18.073 30.2917 24.0105 26.625 27.6667"
              stroke="white"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M22.2012 25.7621C19.5942 26.9778 16.4953 25.8499 15.2797 23.2429C14.064 20.6359 15.1919 17.5371 17.7989 16.3214C20.4059 15.1058 34.1611 14.4384 34.1611 14.4384C34.1611 14.4384 24.8081 24.5465 22.2012 25.7621Z"
              fill="black"
            />
            <path
              d="M21.321 23.8741C19.7569 24.6035 17.8976 23.9268 17.1681 22.3626C16.4387 20.7984 17.1155 18.9391 18.6797 18.2097C20.2439 17.4803 30.3851 16.1994 30.3851 16.1994C30.3851 16.1994 22.8853 23.1447 21.321 23.8741Z"
              fill="white"
            />
          </mask>
          <g mask="url(#mask0_9069_48948)">
            <path d="M32.5 7.5H7.5V32.5H32.5V7.5Z" fill="currentColor" />
          </g>
        </g>
        <defs>
          <clipPath id="clip0_9069_48948">
            <rect width={25} height={25} fill="white" transform="translate(7.5 7.5)" />
          </clipPath>
        </defs>
      </svg>
    ),
  },
];

export type ScheduleFlexibility = {
  name: string;
  value: string;
};

export const trainingScheduleFlexibility: ScheduleFlexibility[] = [
  { name: 'Limited', value: 'limited' },
  { name: 'Moderate', value: 'moderate' },
  { name: 'Flexible', value: 'flexible' },
];

export type Intensity = {
  name: string;
  value: string;
  icon: ReactNode;
};

export const trainingIntensity: Intensity[] = [
  {
    name: 'High intensity',
    value: 'high_intensity',
    icon: (
      <svg
        width={40}
        height={40}
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_9069_49001)">
          <path
            d="M12.4001 18.8892C12.7286 18.4725 13.1285 18.1114 13.4999 17.7503C14.4569 16.9169 15.5424 16.3197 16.4565 15.4447C18.5847 13.4168 19.056 10.0695 17.6991 7.5C19.056 7.81945 20.2415 8.5417 21.2556 9.33339C24.9549 12.2224 26.4118 17.3197 24.6693 21.6948C24.6121 21.8337 24.555 21.9726 24.555 22.1532C24.555 22.4588 24.7693 22.7365 25.0549 22.8477C25.3834 22.9866 25.7262 22.9032 25.9976 22.681C26.0787 22.615 26.1465 22.5349 26.1976 22.4449C27.8116 20.4587 28.0687 17.6114 26.9831 15.3336C29.3684 17.2225 30.6682 20.417 30.4825 23.431C30.3968 24.1255 30.3111 24.8199 30.0683 25.5144C29.8683 26.3478 29.4827 27.1811 29.0542 27.9172C27.5116 30.3201 24.8407 32.0424 21.9698 32.3896C18.9132 32.7646 15.6424 32.2229 13.2999 30.1673C10.6861 27.8617 9.772 24.1671 11.1146 21.0004L11.3003 20.6393C11.6002 20.0004 12.4001 18.8892 12.4001 18.8892ZM16.9135 27.6395C17.3135 27.9728 17.9705 28.3339 18.4847 28.4728C20.0844 29.0284 21.6841 28.2506 22.6268 27.3339C20.9271 26.945 19.913 25.7227 19.6131 24.4866C19.3702 23.3755 19.8273 22.4588 20.013 21.3893C20.1844 20.3615 20.1558 19.4865 19.7702 18.5281C19.4988 19.0559 19.2131 19.5837 18.8703 20.0004C17.7705 21.3893 16.0423 22.0004 15.6709 23.8894C15.6138 24.0838 15.5852 24.2783 15.5852 24.4866C15.5424 25.6255 16.0566 26.8756 16.9135 27.6395Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="clip0_9069_49001">
            <rect width={26} height={26} fill="white" transform="matrix(-1 0 0 1 33.5 7)" />
          </clipPath>
        </defs>
      </svg>
    ),
  },
  {
    name: 'Balanced',
    value: 'balanced',
    icon: (
      <svg
        width={40}
        height={40}
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M33.5327 20.8654H6.46729C6.00173 20.8654 5.625 21.2524 5.625 21.7308C5.625 22.2091 6.00173 22.5962 6.46729 22.5962H18.5123L14.2241 29.9392C13.8874 30.5164 14.2927 31.25 14.9463 31.25H25.0537C25.7061 31.25 26.1132 30.5175 25.7759 29.9392L21.4877 22.5962H33.5327C33.9983 22.5962 34.375 22.2091 34.375 21.7308C34.375 21.2524 33.9983 20.8654 33.5327 20.8654Z"
          fill="currentColor"
        />
        <path
          d="M10.6787 19.1346C13.4698 19.1346 15.7324 16.8099 15.7324 13.9423C15.7324 11.0747 13.4698 8.75 10.6787 8.75C7.88762 8.75 5.625 11.0747 5.625 13.9423C5.625 16.8099 7.88762 19.1346 10.6787 19.1346Z"
          fill="currentColor"
        />
        <path
          d="M29.3213 19.1346C32.1124 19.1346 34.375 16.8099 34.375 13.9423C34.375 11.0747 32.1124 8.75 29.3213 8.75C26.5302 8.75 24.2676 11.0747 24.2676 13.9423C24.2676 16.8099 26.5302 19.1346 29.3213 19.1346Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
  {
    name: 'Low intensity',
    value: 'low_intensity',
    icon: (
      <svg
        width={40}
        height={40}
        viewBox="0 0 40 40"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M19.9999 24.1667C21.7291 24.1667 23.1249 22.7708 23.1249 21.0417C23.1249 19.875 22.4895 18.8542 21.5624 18.3229L11.4478 12.4688L17.2083 22.4479C17.7291 23.4688 18.7812 24.1667 19.9999 24.1667ZM19.9999 10.625C18.1145 10.625 16.3541 11.1458 14.8228 12L17.0103 13.2604C17.9166 12.9063 18.9583 12.7083 19.9999 12.7083C24.6041 12.7083 28.3333 16.4375 28.3333 21.0417C28.3333 23.3438 27.4062 25.4271 25.8958 26.9271H25.8853C25.7888 27.0235 25.7122 27.1379 25.6599 27.2639C25.6076 27.3899 25.5807 27.525 25.5807 27.6615C25.5807 27.7979 25.6076 27.933 25.6599 28.059C25.7122 28.185 25.7888 28.2995 25.8853 28.3958C26.2916 28.8021 26.9583 28.8021 27.3645 28.4063C28.3332 27.4402 29.1016 26.2923 29.6253 25.0285C30.1491 23.7646 30.418 22.4097 30.4166 21.0417C30.4166 15.3125 25.7291 10.625 19.9999 10.625ZM9.58325 21.0417C9.58325 23.9167 10.7499 26.5208 12.6353 28.4063C13.0416 28.8021 13.6978 28.8021 14.1041 28.3958C14.2007 28.2995 14.2773 28.185 14.3295 28.059C14.3818 27.933 14.4087 27.7979 14.4087 27.6615C14.4087 27.525 14.3818 27.3899 14.3295 27.2639C14.2773 27.1379 14.2007 27.0235 14.1041 26.9271C13.3294 26.1556 12.7151 25.2383 12.2968 24.2281C11.8784 23.218 11.6642 22.135 11.6666 21.0417C11.6666 20 11.8645 18.9583 12.2291 18.0208L10.9687 15.8333C10.1041 17.3958 9.58325 19.1458 9.58325 21.0417Z"
          fill="currentColor"
        />
      </svg>
    ),
  },
];
